import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ModuleNotConnected, useRest, RHead, RestLoading, RestPageBrief, money, BRAND, BrandMark } from './restui';
import { AgentHelp } from '../../components/AgentHelp';

export { default as RestModuleMenu } from './RestMenu';
export { default as RestModuleReviews } from './RestReviews';

/* Honest not-yet-connected intelligence modules. Zero fake data, zero fake buttons. */

export function RestModuleGuests() {
  const { isEs } = useOutletContext();
  return <ModuleNotConnected isEs={isEs} title="Guests" testid="rest-module-guests"
    desc_en="Repeat customers, lifetime value, visit frequency and segments." desc_es="Clientes recurrentes, lifetime value, frecuencia de visita y segmentos."
    needs={['POS customer data', 'Loyalty program', 'Online ordering accounts']} />;
}

export function RestModuleMarketing() {
  const { isEs } = useOutletContext();
  const [days, setDays] = useState(30);
  const { data: d, loading } = useRest(`/marketing?days=${days}`);
  if (loading && !d) return <RestLoading />;
  if (!d?.has_data) {
    return <ModuleNotConnected isEs={isEs} title="Marketing" testid="rest-module-marketing"
      desc_en="Website, SEO, Google Business, email campaigns and social performance." desc_es="Website, SEO, Google Business, email y redes sociales."
      needs={['Google Business Profile', 'Google Analytics', 'Search Console', 'Email platform']} />;
  }
  const k = d.kitxens_driven;
  const maxCh = Math.max(...d.channels.map((c) => c.total), 1);
  return (
    <div data-testid="rest-marketing">
      <RHead title="Marketing"
        sub={`${d.window.from} → ${d.window.to}`}
        right={
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="rest-mkt-range"
            className="h-9 px-3 rounded-full border border-gray-200 bg-white text-[13px] font-semibold text-gray-600 focus:outline-none cursor-pointer">
            {[[7, isEs ? '7 días' : '7 days'], [30, isEs ? '30 días' : '30 days'], [90, isEs ? '90 días' : '90 days'], [365, isEs ? '12 meses' : '12 months']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        } />

      {k && (
        <RestPageBrief isEs={isEs} testid="rest-mkt-brief"
          summary={isEs
            ? `El marketing automatizado de Kitxens generó ${money(k.sales)} (${k.pct}% de tus ventas) en este periodo, con ${d.campaigns.filter((c) => c.status === 'on').length} campañas activas y ${d.subscribers?.toLocaleString()} suscriptores.`
            : `Kitxens automated marketing drove ${money(k.sales)} (${k.pct}% of your sales) this period, with ${d.campaigns.filter((c) => c.status === 'on').length} active campaigns and ${d.subscribers?.toLocaleString()} subscribers.`}
          kpi={{ label: isEs ? 'Ventas por Kitxens' : 'Sales driven by Kitxens', value: money(k.sales), sub: `${k.orders.toLocaleString()} ${isEs ? 'órdenes' : 'orders'} · ${k.pct}%` }} />
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 mb-5" data-testid="rest-mkt-channels">
        <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-4">{isEs ? 'Ventas por canal' : 'Sales by channel'}</p>
        <div className="space-y-3.5">
          {d.channels.map((c) => (
            <div key={c.key} data-testid={`rest-mkt-channel-${c.key}`}>
              <div className="flex items-center justify-between gap-2 text-[13px]">
                <span className="font-semibold text-gray-800 inline-flex items-center gap-2">
                  <BrandMark k={c.key} size={13} />
                  {(BRAND[c.key] || { label: c.key }).label}
                </span>
                <span className="tabular-nums text-gray-900 font-bold">{money(c.total)} <span className="text-gray-400 font-semibold text-[11.5px]">· {c.share_pct}%</span></span>
              </div>
              <div className="mt-1.5 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full kx-grow-x" style={{ width: `${Math.max(3, 100 * c.total / maxCh)}%`, background: (BRAND[c.key] || {}).color || '#9ca3af' }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden" data-testid="rest-mkt-campaigns">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
          <p className="text-[15px] font-bold text-gray-900 flex items-center gap-1.5">{isEs ? 'Marketing automatizado' : 'Automated marketing'} <span className="text-[11px] font-bold text-[var(--rest-accent)] inline-flex items-center gap-1">· Penny <AgentHelp agent="penny" isEs={isEs} /></span></p>
          <p className="text-[12px] text-gray-400">{isEs ? 'Campañas de email y SMS diseñadas y enviadas por Kitxens en tu nombre.' : 'Email and text campaigns designed and sent on your behalf by Kitxens.'}</p>
        </div>
        {d.campaigns_available ? (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-wider text-gray-400">
                    {[isEs ? 'Campaña' : 'Campaign', isEs ? 'Ventas' : 'Sales', isEs ? 'Órdenes' : 'Orders', 'Click rate', isEs ? 'Apertura' : 'Opened', isEs ? 'Entregados' : 'Delivered', isEs ? 'Estado' : 'Status'].map((h, i) => (
                      <th key={i} className={`px-4 lg:px-6 py-3 font-bold ${i > 0 ? 'text-right' : ''}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.campaigns.map((c) => (
                    <tr key={c.key} className="border-t border-gray-50 hover:bg-orange-50/40 transition-colors" data-testid={`rest-mkt-camp-${c.key}`}>
                      <td className="px-4 lg:px-6 py-3.5 font-semibold text-gray-900">{c.name}
                        <span className="ml-2 text-[10px] uppercase font-bold text-gray-300">{c.channel}</span></td>
                      <td className="px-4 lg:px-6 py-3.5 text-right tabular-nums font-bold text-gray-900">{money(c.sales)}</td>
                      <td className="px-4 lg:px-6 py-3.5 text-right tabular-nums text-gray-600">{c.orders.toLocaleString()}</td>
                      <td className="px-4 lg:px-6 py-3.5 text-right tabular-nums text-gray-600">{c.click_rate}%</td>
                      <td className="px-4 lg:px-6 py-3.5 text-right tabular-nums text-gray-600">{c.open_rate}%</td>
                      <td className="px-4 lg:px-6 py-3.5 text-right tabular-nums text-gray-600">{c.delivered.toLocaleString()}</td>
                      <td className="px-4 lg:px-6 py-3.5 text-right">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> On
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden divide-y divide-gray-50">
              {d.campaigns.map((c) => (
                <div key={c.key} className="px-4 py-3.5" data-testid={`rest-mkt-camp-m-${c.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-gray-900 text-[13.5px] leading-snug">{c.name}</p>
                    <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10.5px] font-bold text-emerald-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> On
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-gray-500 tabular-nums">
                    <b className="text-gray-900 text-[13px]">{money(c.sales)}</b>
                    <span>{c.orders} {isEs ? 'órd.' : 'ord.'}</span>
                    <span>{c.click_rate}% clicks</span>
                    <span>{c.open_rate}% {isEs ? 'apertura' : 'opened'}</span>
                    <span>{c.delivered} {isEs ? 'entregados' : 'delivered'}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="px-6 py-8 text-center">
            <p className="text-[13.5px] font-semibold text-gray-700">{isEs ? 'Las campañas automatizadas se activan con Kitxens Marketing.' : 'Automated campaigns activate with Kitxens Marketing.'}</p>
            <p className="mt-1 text-[12px] text-gray-400">{isEs ? 'Tu desglose por canal ya funciona con tus datos reales. Habla con tu asesor para activar campañas.' : 'Your channel breakdown already runs on your real data. Talk to your advisor to activate campaigns.'}</p>
          </div>
        )}
      </div>
    </div>
  );
}




